import { zodResolver } from "@hookform/resolvers/zod";
import Divider from "@mui/material/Divider";
import classNames from "classnames";
import { SyntheticEvent, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  ReactQueryKeysAccount,
  ReactQueryKeysGlobal,
} from "services/queryKeys";
import { createSecret } from "services/serviceAccounts/createSecret";
import { createServiceAccount } from "services/serviceAccounts/createServiceAccount";
import { deleteServiceAccount } from "services/serviceAccounts/deleteServiceAccount";
import { getServiceAccounts } from "services/serviceAccounts/getServiceAccounts";
import {
  EMPTY_NETWORK_POLICY,
  ServiceAccount,
} from "services/serviceAccounts/types";
import { createUser } from "services/users/createUser";
import { updateUserServiceAccount } from "services/users/updateUserServiceAccount";

import { useCurrentAccount } from "components/Account/useCurrentAccount";
import { useCopy } from "components/CopyProvider";
import FormRow from "components/FormRow";
import formRowStyles from "components/FormRow/FormRow.module.scss";
import { CheckIconSmall, CopyIcon } from "components/Icons";
import { Modal } from "components/Modal/Modal";
import {
  InlineNotification,
  NotificationType,
} from "components/Notification/InlineNotification";
import {
  StatusMessagePosition,
  StatusMessageType,
} from "components/StatusMessageQueue/StatusMessageQueueProvider";
import useStatusMessageQueue from "components/StatusMessageQueue/hooks/useStatusMessageQueue";
import { TextInput } from "components/TextInput/TextInput";
import Toggle from "components/Toggle";
import Tooltip from "components/Tooltip";
import { queryClient } from "components/queryClient";

import { ServiceAccountModal } from "./Modal";
import { AssignType, Fields, schema } from "./schema";

import styles from "./styles.module.scss";

export type CreatedPayload = {
  serviceAccountName: string;
  serviceAccountId: string;
  createdUser?: string;
  secret?: string;
};

const CopySecret = (props: { secret: string }) => {
  const { secret } = props;
  const { copied, handleCopy } = useCopy();

  return (
    <div
      className={classNames(styles.inputCopyIcon, styles.multiLine)}
      onClick={() => {
        handleCopy(secret);
      }}
    >
      <Tooltip
        title="Copied"
        open={!!copied}
      >
        <span className={styles.icon}>
          {copied ? (
            <CheckIconSmall
              data-testid="copied-icon"
              className={styles.greenIcon}
            />
          ) : (
            <CopyIcon />
          )}
        </span>
      </Tooltip>
    </div>
  );
};

export const SecretContent = (props: {
  onClose: () => void;
  createdPayload: CreatedPayload | null;
  onFinished?: (payload: CreatedPayload) => void;
}) => {
  const { onClose, createdPayload, onFinished } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const { t } = useTranslation();
  const { copied, handleCopy } = useCopy();

  const { putStatusMessage } = useStatusMessageQueue();

  const finish = (event?: SyntheticEvent) => {
    event?.preventDefault?.();
    if (createdPayload) {
      onFinished?.({
        ...createdPayload,
        secret: secret ?? undefined,
      });
    }
    onClose();
  };

  const handleClose = (
    _event: {},
    reason?: "backdropClick" | "escapeKeyDown"
  ) => {
    if (secret && (reason === "backdropClick" || reason === "escapeKeyDown")) {
      return;
    }
    finish();
  };

  const handleGenerateSecret = async () => {
    try {
      setIsLoading(true);
      const secret = await createSecret(createdPayload as ServiceAccount);

      setSecret(secret);
      setIsLoading(false);
    } catch (error: any) {
      putStatusMessage({
        type: StatusMessageType.Error,
        message: error.message,
        options: {
          id: "start",
          insertToPosition: StatusMessagePosition.Top,
          autoRemove: true,
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      title={t("serviceAccounts.modal.title")}
      onClose={handleClose}
      primaryButton={{
        text: t("serviceAccounts.modal.done"),
        onSubmit: finish,
        isLoading,
        disabled: isLoading,
      }}
    >
      <div className={styles.createdEntities}>
        <div>
          {t("serviceAccounts.modal.created", {
            name: createdPayload?.serviceAccountName,
          })}
        </div>
        {createdPayload?.createdUser && (
          <div>
            {t("serviceAccounts.modal.created_user", {
              name: createdPayload?.createdUser,
            })}
          </div>
        )}
      </div>
      <Divider className={styles.divider} />
      <div className={styles.secret__id}>
        <FormRow
          label={t("serviceAccounts.modal.id")}
          input={
            <TextInput
              value={createdPayload?.serviceAccountId}
              inputRootClassName={formRowStyles.nameInput}
              className={formRowStyles.nameInputContainer}
              autoComplete="off"
              endAdornment={
                <div
                  className={styles.inputCopyIcon}
                  onClick={() => {
                    handleCopy(createdPayload?.serviceAccountId ?? "");
                  }}
                >
                  <Tooltip
                    title="Copied"
                    open={!!copied}
                  >
                    <span className={styles.icon}>
                      {copied ? (
                        <CheckIconSmall
                          data-testid="copied-icon"
                          className={styles.greenIcon}
                        />
                      ) : (
                        <CopyIcon />
                      )}
                    </span>
                  </Tooltip>
                </div>
              }
              disabled
            />
          }
        />
      </div>
      <Divider className={styles.divider} />
      <div className={styles.secret__generate}>
        <FormRow
          classes={{ inputContainer: styles.toggle }}
          label={t("serviceAccounts.modal.generate")}
          input={
            <Toggle
              size="sm"
              dataTestId="genrate-secret"
              checked={!!secret}
              disabled={!!secret}
              classes={{ wrapper: styles.toggleWrapper }}
              onChange={handleGenerateSecret}
              tightRight
            />
          }
        />
        <InlineNotification
          className={styles.notification}
          type={NotificationType.Info}
          title={t("serviceAccounts.modal.notification_title")}
          body={t("serviceAccounts.modal.notification_body")}
        />

        {secret && (
          <FormRow
            label={t("serviceAccounts.modal.new_secret")}
            input={
              <TextInput
                value={secret}
                disabled
                testId="secret"
                inputRootClassName={styles.secret__content}
                className={formRowStyles.nameInputContainer}
                autoComplete="off"
                multiline
                endAdornment={<CopySecret secret={secret} />}
              />
            }
          />
        )}
      </div>
    </Modal>
  );
};

const ServiceAccountContent = (props: {
  onClose: () => void;
  onCreate: (data: CreatedPayload) => void;
  allowedAccountNames?: string[];
}) => {
  const { onClose, onCreate, allowedAccountNames } = props;
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { t } = useTranslation();
  const { getAccount } = useCurrentAccount();
  const { putStatusMessage } = useStatusMessageQueue();

  const currentAccount = getAccount();
  const form = useForm<Fields>({
    resolver: zodResolver(schema),
    defaultValues: {
      networkPolicyName: EMPTY_NETWORK_POLICY,
      isAdmin: false,
      accountName: currentAccount.accountName,
      assignType: AssignType.assign,
      assign: {
        [currentAccount.accountName]: "",
      },
    },
  });

  const onFormSubmit = async (data: Fields) => {
    if (isLoading) {
      return;
    }

    if (!data.assign[data.accountName]) {
      form.setError("assign", {
        [data.accountName]: {
          type: "required",
          message: "User name is required",
        },
      });
      return;
    }
    let serviceAccountCreated = false;
    try {
      setIsLoading(true);
      await createServiceAccount(
        {
          name: data.name,
          description: data.description,
          networkPolicyName: data.networkPolicyName,
          isOrganizationAdmin: data.isAdmin,
        },
        data.accountName
      );
      serviceAccountCreated = true;
      if (data.assignType === AssignType.create) {
        await createUser(
          {
            userName: data.userName,
            defaultDatabase: data.defaultDatabase,
            defaultEngine: data.defaultEngine,
            serviceAccountName: data.name,
            roles: data.roles ?? [],
          },
          data.accountName
        );
      } else {
        await updateUserServiceAccount(
          data.assign[data.accountName],
          data.name,
          data.accountName
        );
      }
      const [serviceAccount] = await getServiceAccounts(data.name);
      if (!serviceAccount) {
        throw new Error("Service account not found");
      }
      onCreate({
        serviceAccountName: serviceAccount.serviceAccountName,
        serviceAccountId: serviceAccount.serviceAccountId,
        createdUser: data.userName,
      });
      await queryClient.invalidateQueries({
        queryKey: [ReactQueryKeysGlobal.serviceAccounts],
      });
      await queryClient.invalidateQueries({
        queryKey: [ReactQueryKeysAccount.users],
      });
    } catch (error: any) {
      putStatusMessage({
        type: StatusMessageType.Error,
        message: error.message,
        options: {
          id: "start",
          insertToPosition: StatusMessagePosition.Top,
          autoRemove: true,
        },
      });
      if (serviceAccountCreated) {
        await deleteServiceAccount(data.name);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ServiceAccountModal
      form={form}
      isLoading={isLoading}
      onClose={onClose}
      onFormSubmit={onFormSubmit}
      title={t("serviceAccounts.modal.title")}
      modalAction={t("serviceAccounts.modal.action")}
      allowedAccountNames={allowedAccountNames}
    />
  );
};

export const CreateServiceAccount = (props: {
  onClose: () => void;
  allowedAccountNames?: string[];
  onCompleted?: (payload: CreatedPayload) => void;
}) => {
  const { onClose, allowedAccountNames, onCompleted } = props;
  const [step, setStep] = useState("create");
  const [createdPayload, setCreatedPayload] = useState<CreatedPayload | null>(
    null
  );

  const onCreate = (data: CreatedPayload) => {
    setCreatedPayload(data);
    setStep("secret");
  };

  if (step === "secret") {
    return (
      <SecretContent
        onClose={onClose}
        createdPayload={createdPayload}
        onFinished={payload => {
          onCompleted?.(payload);
        }}
      />
    );
  }

  return (
    <ServiceAccountContent
      onClose={onClose}
      onCreate={onCreate}
      allowedAccountNames={allowedAccountNames}
    />
  );
};

export default SecretContent;
